import React, { useState } from 'react';
import { Row, Col } from 'react-bootstrap';
import { useQuery, useMutation } from '@apollo/client';
import { GET_WATER_USE_SYSTEM_OPERATIONAL_DAYS, SET_WATER_USE_OPERATIONAL_DAYS } from '../../../../../queries/engineeringQrys';
import {
    Modal,
    ModalTitle,
    ModalContent,
    ModalFooter,
    TextInput,
} from '@utahdts/utah-design-system';
import '../../dwri.css';

const DwriOperationalDays = ({ pwsId, years }) => {
    const user = localStorage.getItem("user")
        ? JSON.parse(localStorage.getItem("user"))
        : { isPublic: true };

    const canEditWaterUseSystem = user?.permissions?.includes('edit_water_use_system');


    const [modalShow, setModalShow] = useState(false);
    const [editRow, setEditRow] = useState(null);
    const [daysValue, setDaysValue] = useState('');
    const [daysError, setDaysError] = useState('');
    const [mutationError, setMutationError] = useState('');

    const [setOperationalDays] = useMutation(SET_WATER_USE_OPERATIONAL_DAYS, {
        onCompleted: () => {
            // Refetch the operational days after successful update
            refetch();
        },
        onError: (error) => {
            setMutationError(error.message || 'Failed to update operational days.');
        }
    });

    const { data, loading, error, refetch } = useQuery(GET_WATER_USE_SYSTEM_OPERATIONAL_DAYS, {
        variables: { pwsId, years },
        skip: !pwsId || !years || years.length === 0,
        fetchPolicy: 'cache-and-network',
    });

    if (error) return <p className="text-danger">Error loading Operational Days</p>;
    if (loading) return <p>Loading...</p>;

    const rows = data?.getWaterUseSystemOperationalDays || [];

    function isLeapYear(year) {
        return (year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0));
    }

    function handleEditClick(row) {
        setEditRow(row);
        const year = row.year;
        const maxDays = isLeapYear(year) ? 366 : 365;
        // If no value in DB, default to days in year
        setDaysValue(row.operationalDays === undefined || row.operationalDays === null ? maxDays : row.operationalDays);
        setDaysError('');
        setModalShow(true);
    }

    function handleModalClose() {
        setModalShow(false);
        setEditRow(null);
        setDaysValue('');
        setDaysError('');
    }

    function handleDaysChange(e) {
        const val = e.target.value;
        // Only allow numbers
        if (/^\d{0,3}$/.test(val)) {
            setDaysValue(val);
            setDaysError('');
        }
    }

    async function handleSave() {
        if (!editRow) return;
        const year = editRow.year;
        const maxDays = isLeapYear(year) ? 366 : 365;
        const num = Number(daysValue);
        if (daysValue === '' || isNaN(num) || num < 0 || num > maxDays) {
            setDaysError(`Value must be between 0 and ${maxDays}`);
            return;
        }
        setMutationError('');
        try {
            await setOperationalDays({
                variables: {
                    pwsId,
                    year,
                    operationalDays: num
                }
            });
            handleModalClose();
        } catch (err) {
            // Error handled in onError
        }
    }

    return (
        <>
            <Row>
                <Col><span><b>Operational Days</b></span></Col>
            </Row>
            {mutationError && (
                <Row>
                    <Col>
                        <div className="text-danger mb-2">{mutationError}</div>
                    </Col>
                </Row>
            )}
            <div className="table-responsive">
                <Row className={`dwri-operational-days-header gx-0${canEditWaterUseSystem ? ' has-edit' : ''}`}> 
                    <Col className='dwri-table-header-cell dwri-col-year'><span><b>Year</b></span></Col>
                    <Col className='dwri-table-header-cell dwri-col-days'><span><b>Days</b></span></Col>
                    {canEditWaterUseSystem && (
                        <Col className='dwri-table-header-cell dwri-col-edit'><span><b>Edit</b></span></Col>
                    )}
                </Row>
                {rows.map((row, idx) => (
                    <Row key={row.year} className={`dwri-operational-days-row gx-0${canEditWaterUseSystem ? ' has-edit' : ''} ${idx % 2 === 0 ? 'is-even' : 'is-odd'}`}>
                        <Col className='dwri-table-cell dwri-col-year'><span>{row.year}</span></Col>
                        <Col className='dwri-table-cell dwri-col-days'><span>{row.operationalDays}</span></Col>
                        {canEditWaterUseSystem && (
                            <Col className='dwri-table-cell dwri-col-edit'>
                                <button
                                    className="link-button"
                                    aria-label={`Edit operational days for year ${row.year}`}
                                    onClick={() => handleEditClick(row)}
                                    type="button"
                                >
                                    Edit
                                </button>
                            </Col>
                        )}
                    </Row>
                ))}
            </div>
            {modalShow && editRow && (
                <Modal
                    onClose={handleModalClose}
                    onEscape={handleModalClose}
                >
                    <ModalTitle>Update Operational Days</ModalTitle>
                    <ModalContent>
                        <div className="dwri-operational-modal-grid">
                            <TextInput
                                id="dwri-operational-days-year"
                                label="Year"
                                value={String(editRow.year)}
                                isDisabled
                            />
                            <div>
                                <TextInput
                                    id="dwri-operational-days-value"
                                    label="Operational Days"
                                    value={String(daysValue)}
                                    onChange={handleDaysChange}
                                    errorMessage={daysError || undefined}
                                    isRequired
                                />
                                <div className="dwri-operational-modal-hint">
                                    Max: {isLeapYear(editRow.year) ? 366 : 365} days
                                </div>
                            </div>
                        </div>
                    </ModalContent>
                    <ModalFooter className="dwri-service-modal-footer">
                        <button type="button" onClick={handleModalClose}>Close</button>
                        <button type="button" onClick={handleSave}>Save</button>
                    </ModalFooter>
                </Modal>
            )}
        </>
    );
};

export default DwriOperationalDays;